import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Divider, Skeleton, Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import RoundedBox from '@sharedCR/common/RoundedBox';
import { getCommonDate } from 'app/pages/loanApplication/LoanApplication.utils';
import { IconCalendarClosed } from 'assets/icons';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { LoanType } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';
import { getConditionalArray } from 'utils/data';
import { checkIsChattelLoanPurpose, checkIsChattelLoanType } from 'utils/loanApplication';

import { selectCommonInfoLoanApplication, selectKeyDates } from 'store/loanApplications/selectors';

const KeyDates = ({ isLoading }) => {
  const keyDatesData = useSelector(selectKeyDates);
  const { loanType, loanPurpose } = useSelector(selectCommonInfoLoanApplication);
  const getModifiedFieldAuthors = useModifiedFieldPendingChangeAuthors();

  const isEcdHidden = [undefined, LoanType.HELOC].includes(loanType);
  // Chattel / manufactured-home loans are not subject to TRID, so hide the TRID key date (LEN-44).
  const isTridHidden = checkIsChattelLoanType(loanType) || checkIsChattelLoanPurpose(loanPurpose);
  const { modifiedFields } = keyDatesData;

  const keyDatesConfig = useMemo(
    () => [
      ...getConditionalArray(!isTridHidden, [
        {
          title: 'TRID',
          nameInConfig: 'trid',
          data: '-',
        },
      ]),
      ...getConditionalArray(!isEcdHidden, [
        {
          title: 'Earliest Closing',
          nameInConfig: 'earliestClosing',
          data: '-',
        },
      ]),
    ],
    [isEcdHidden, isTridHidden],
  );

  return (
    <div className="flex-grow flex flex-col gap-8 h-fit">
      <Typography variant="medium16">Key Dates</Typography>
      <RoundedBox>
        {keyDatesConfig.map((item, index) => (
          <div key={index}>
            {isLoading ? (
              <Skeleton height={24} variant="text" width="100%" />
            ) : (
              <div className="flex justify-between items-center">
                <div className="flex gap-4 items-center">
                  <IconCalendarClosed color="text.dark" />
                  <Typography color="text.dark" variant="regular14">
                    {item.title}:
                  </Typography>
                </div>
                <PendingChangeWrapper
                  isSmall
                  authors={getModifiedFieldAuthors(
                    keyDatesData.modifiedFieldDetails,
                    item.nameInConfig,
                  )}
                  fieldLabel={item.title}
                  pendingChangesActionType={
                    modifiedFields?.includes(item.nameInConfig)
                      ? PendingChangesActionType.MODIFIED
                      : undefined
                  }
                  textSelector="span"
                >
                  <Typography variant="bold16">
                    {getCommonDate(keyDatesData[item.nameInConfig]) || item.data}
                  </Typography>
                </PendingChangeWrapper>
              </div>
            )}
            {index !== keyDatesConfig.length - 1 && (
              <div className="my-12">
                <Divider />
              </div>
            )}
          </div>
        ))}
      </RoundedBox>
    </div>
  );
};

KeyDates.propTypes = {
  ...WithLoadingPropTypes,
};

export default KeyDates;
